import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../shadcn/ui/dialog";
import { Button } from "../shadcn/ui/button";
import { Calendar } from "../shadcn/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../shadcn/ui/popover";
import { CalendarIcon } from "lucide-react";
import billingService from "../services/billingService";
import logger from "../utils/logger";
import moment from "moment-timezone";
import { ChevronDown, ChevronUp, DollarSign, TrendingUp, Users, Building, PieChart } from "lucide-react";

/**
 * Revenue Report Modal - Displays detailed platform revenue breakdown
 *
 * Shows:
 * - Collections (money coming in)
 * - Revenue by source
 * - Revenue share breakdown
 * - Revenue by category
 * - Revenue by customer type
 */
const RevenueReportModal = ({ isOpen, onClose, initialStartDate, initialEndDate }) => {
  // Date range state - default to last 30 days if not provided
  // Store as Date objects for DatePicker, and as strings for API calls
  const [startDateObj, setStartDateObj] = useState(
    initialStartDate ? new Date(initialStartDate) : moment().subtract(30, "days").toDate()
  );
  const [endDateObj, setEndDateObj] = useState(initialEndDate ? new Date(initialEndDate) : new Date());

  // Helper to format date for API
  const formatDateForApi = date => moment(date).format("YYYY-MM-DD");
  const startDate = formatDateForApi(startDateObj);
  const endDate = formatDateForApi(endDateObj);

  // Data state
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Expanded sections state
  const [expandedSections, setExpandedSections] = useState({
    collections: true,
    bookingRevenue: true,
    revenueShare: true,
    breakdown: true,
  });

  // Fetch report data
  const fetchReport = async () => {
    // Validate dates before making API call
    if (startDateObj && endDateObj && startDateObj > endDateObj) {
      setError("End date must be greater than or equal to start date");
      return;
    }

    setLoading(true);
    setError(null);
    try {
      const response = await billingService.getPlatformRevenueReport({
        startDate,
        endDate,
      });

      if (response.status === 200) {
        setReportData(response.data?.data);
        logger.log("Revenue report data:", response.data?.data);
      }
    } catch (err) {
      logger.error("Error fetching revenue report:", err);

      // Handle validation error from API
      if (err.response?.status === 400 && err.response?.data?.details) {
        const details = err.response.data.details;
        if (details.some(d => d.includes("endDate") && d.includes("startDate"))) {
          setError("End date must be greater than or equal to start date");
        } else {
          setError(details.join(", "));
        }
      } else {
        setError("Failed to load revenue report. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch on mount and when dates change
  useEffect(() => {
    if (isOpen) {
      fetchReport();
    }
  }, [isOpen, startDate, endDate]);

  // Toggle section expansion
  const toggleSection = section => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  // Format currency amount
  const formatAmount = (amount, currencyCode = "KES") => {
    const num = parseFloat(amount || 0);
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currencyCode,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(num);
  };

  // Format number with commas
  const formatNumber = num => {
    return new Intl.NumberFormat("en-US").format(num);
  };

  // Render currency breakdown
  const renderCurrencyBreakdown = (currencies, title) => {
    if (!currencies || currencies.length === 0) {
      return <span className="text-gray-400 text-sm">No data</span>;
    }

    return currencies.map((curr, idx) => (
      <div key={idx} className="flex justify-between items-center py-1">
        <span className="text-gray-600">{curr.currency?.code || "KES"}</span>
        <div className="text-right">
          <span className="font-semibold text-gray-900">{formatAmount(curr.amount, curr.currency?.code)}</span>
          <span className="text-gray-400 text-sm ml-2">({formatNumber(curr.count)} transactions)</span>
        </div>
      </div>
    ));
  };

  // Calculate total from currencies array
  const calculateTotal = currencies => {
    if (!currencies || currencies.length === 0) return { amount: 0, count: 0 };

    return currencies.reduce(
      (acc, curr) => ({
        amount: acc.amount + parseFloat(curr.amount || 0),
        count: acc.count + (curr.count || 0),
      }),
      { amount: 0, count: 0 }
    );
  };

  // Render breakdown section (for serviceCategory and organizationType)
  const renderBreakdown = (breakdownData, byType) => {
    const breakdown = breakdownData?.find(b => b.by === byType);
    if (!breakdown || !breakdown.data || breakdown.data.length === 0) {
      return <span className="text-gray-400 text-sm">No data available</span>;
    }

    return (
      <div className="space-y-2">
        {breakdown.data.map((item, idx) => {
          const total = calculateTotal(item.currencies);
          return (
            <div key={idx} className="flex justify-between items-center py-2 border-b border-gray-100 last:border-0">
              <div className="flex items-center gap-2">
                <span
                  className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                    byType === "serviceCategory" ? "bg-blue-100 text-blue-700" : "bg-purple-100 text-purple-700"
                  }`}
                >
                  {item.item?.code || item.item?.name || "Unknown"}
                </span>
                <span className="text-gray-600 text-sm">{item.item?.name}</span>
              </div>
              <div className="text-right">
                <span className="font-semibold text-gray-900">
                  {formatAmount(total.amount, item.currencies?.[0]?.currency?.code)}
                </span>
                <span className="text-gray-400 text-sm ml-2">({formatNumber(item.totalCount)} transactions)</span>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl font-bold text-gray-900 flex items-center justify-center gap-2">
            <DollarSign className="w-6 h-6 text-green-600" />
            Platform Commission Revenue Report
          </DialogTitle>
          <p className="text-sm text-gray-500 text-center">Detailed breakdown of platform revenue</p>
        </DialogHeader>

        {/* Date Range Selector */}
        <div className="bg-gray-50 p-4 rounded-lg mb-6">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {startDateObj ? moment(startDateObj).format("MMM D, YYYY") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={startDateObj} onSelect={setStartDateObj} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <div className="flex-1 min-w-[150px]">
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {endDateObj ? moment(endDateObj).format("MMM D, YYYY") : "Pick a date"}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar mode="single" selected={endDateObj} onSelect={setEndDateObj} initialFocus />
                </PopoverContent>
              </Popover>
            </div>
            <Button onClick={fetchReport} disabled={loading} className="bg-blue-600 hover:bg-blue-700">
              {loading ? "Loading..." : "Apply"}
            </Button>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            Showing data from {moment(startDate).format("MMMM D, YYYY")} to {moment(endDate).format("MMMM D, YYYY")}
          </p>
        </div>

        {/* Error State */}
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">{error}</div>}

        {/* Loading State */}
        {loading && (
          <div className="space-y-4">
            {/* Summary Cards Shimmer */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white border border-gray-200 rounded-xl p-4 animate-pulse">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-5 h-5 bg-gray-200 rounded"></div>
                    <div className="h-4 bg-gray-200 rounded w-24"></div>
                  </div>
                  <div className="h-8 bg-gray-200 rounded w-32 mb-2"></div>
                  <div className="h-3 bg-gray-200 rounded w-40"></div>
                </div>
              ))}
            </div>

            {/* Sections Shimmer */}
            {[1, 2, 3].map(i => (
              <div key={i} className="border border-gray-200 rounded-xl overflow-hidden animate-pulse">
                <div className="p-4 bg-gray-50">
                  <div className="flex items-center gap-2">
                    <div className="w-5 h-5 bg-gray-200 rounded"></div>
                    <div className="h-5 bg-gray-200 rounded w-32"></div>
                  </div>
                </div>
                <div className="p-4 space-y-3">
                  {[1, 2, 3].map(j => (
                    <div key={j} className="flex justify-between items-center">
                      <div className="h-4 bg-gray-200 rounded w-24"></div>
                      <div className="h-4 bg-gray-200 rounded w-32"></div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Report Content */}
        {!loading && reportData && (
          <div className="space-y-4">
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {/* Total Collections */}
              <div className="bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <span className="text-sm font-medium text-green-700">Total Collections</span>
                </div>
                <div className="text-2xl font-bold text-green-800">
                  {(() => {
                    const collections = reportData.collections;
                    const total = calculateTotal([
                      ...(collections?.saasSubscriptions?.currencies || []),
                      ...(collections?.nonSaasSubscriptions?.currencies || []),
                      ...(collections?.directBookings?.currencies || []),
                    ]);
                    return formatAmount(total.amount);
                  })()}
                </div>
                <p className="text-xs text-green-600 mt-1">Money collected from customers</p>
              </div>

              {/* Platform Revenue */}
              <div className="bg-gradient-to-br from-blue-50 to-cyan-50 border border-blue-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <DollarSign className="w-5 h-5 text-blue-600" />
                  <span className="text-sm font-medium text-blue-700">Platform Commission Revenue</span>
                </div>
                <div className="text-2xl font-bold text-blue-800">
                  {(() => {
                    const platform = calculateTotal(reportData.revenueShare?.platform);
                    return formatAmount(platform.amount);
                  })()}
                </div>
                <p className="text-xs text-blue-600 mt-1">Spacia's commission</p>
              </div>

              {/* Provider Payouts */}
              <div className="bg-gradient-to-br from-purple-50 to-violet-50 border border-purple-200 rounded-xl p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users className="w-5 h-5 text-purple-600" />
                  <span className="text-sm font-medium text-purple-700">Provider Payouts</span>
                </div>
                <div className="text-2xl font-bold text-purple-800">
                  {(() => {
                    const provider = calculateTotal(reportData.revenueShare?.provider);
                    return formatAmount(provider.amount);
                  })()}
                </div>
                <p className="text-xs text-purple-600 mt-1">Payouts to hosts/providers</p>
              </div>
            </div>

            {/* Collections Section */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("collections")}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <TrendingUp className="w-5 h-5 text-green-600" />
                  <span className="font-semibold text-gray-900">Collections</span>
                  <span className="text-sm text-gray-500">(Money collected from customers)</span>
                </div>
                {expandedSections.collections ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>

              {expandedSections.collections && (
                <div className="p-4 space-y-4">
                  {/* SaaS Subscriptions */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      SaaS Subscriptions
                    </h4>
                    {renderCurrencyBreakdown(reportData.collections?.saasSubscriptions?.currencies)}
                  </div>

                  {/* Non-SaaS Subscriptions */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                      Non-SaaS Subscriptions
                    </h4>
                    {renderCurrencyBreakdown(reportData.collections?.nonSaasSubscriptions?.currencies)}
                  </div>

                  {/* Direct Bookings */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                      Payments for Direct Bookings
                    </h4>
                    {renderCurrencyBreakdown(reportData.collections?.directBookings?.currencies)}
                  </div>
                </div>
              )}
            </div>

            {/* Booking Revenue Section */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("bookingRevenue")}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <Building className="w-5 h-5 text-blue-600" />
                  <span className="font-semibold text-gray-900">Revenue by Source</span>
                  <span className="text-sm text-gray-500">(Where revenue originates)</span>
                </div>
                {expandedSections.bookingRevenue ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>

              {expandedSections.bookingRevenue && (
                <div className="p-4 space-y-4">
                  {/* Direct Bookings Revenue */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                      Direct Bookings Revenue
                    </h4>
                    {renderCurrencyBreakdown(reportData.bookingRevenue?.directBookings?.currencies)}
                  </div>

                  {/* Subscription Revenue */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 bg-teal-500 rounded-full"></span>
                      Subscription Utilisation
                    </h4>
                    {renderCurrencyBreakdown(reportData.bookingRevenue?.subscriptions?.currencies)}
                  </div>
                </div>
              )}
            </div>

            {/* Revenue Share Section */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("revenueShare")}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-purple-600" />
                  <span className="font-semibold text-gray-900">Revenue Share</span>
                  <span className="text-sm text-gray-500">(How revenue is split)</span>
                </div>
                {expandedSections.revenueShare ? (
                  <ChevronUp className="w-5 h-5" />
                ) : (
                  <ChevronDown className="w-5 h-5" />
                )}
              </button>

              {expandedSections.revenueShare && (
                <div className="p-4 space-y-4">
                  {/* Provider Payouts */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 bg-indigo-500 rounded-full"></span>
                      Payouts to Hosts/Providers
                    </h4>
                    {reportData.revenueShare?.provider?.map((curr, idx) => (
                      <div key={idx} className="flex justify-between items-center py-1">
                        <span className="text-gray-600">{curr.currency?.code || "KES"}</span>
                        <div className="text-right">
                          <span className="font-semibold text-gray-900">
                            {formatAmount(curr.amount, curr.currency?.code)}
                          </span>
                          <span className="text-gray-400 text-sm ml-2">({formatNumber(curr.count)} transactions)</span>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Platform Commission */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-2 flex items-center gap-2">
                      <span className="w-2 h-2 bg-yellow-500 rounded-full"></span>
                      Commission to Spacia
                    </h4>
                    {reportData.revenueShare?.platform?.map((curr, idx) => (
                      <div key={idx} className="flex justify-between items-center py-1">
                        <span className="text-gray-600">{curr.currency?.code || "KES"}</span>
                        <div className="text-right">
                          <span className="font-semibold text-gray-900">
                            {formatAmount(curr.amount, curr.currency?.code)}
                          </span>
                          <span className="text-gray-400 text-sm ml-2">({formatNumber(curr.count)} transactions)</span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Breakdown Section */}
            <div className="border border-gray-200 rounded-xl overflow-hidden">
              <button
                onClick={() => toggleSection("breakdown")}
                className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center gap-2">
                  <PieChart className="w-5 h-5 text-orange-600" />
                  <span className="font-semibold text-gray-900">Revenue Breakdown</span>
                  <span className="text-sm text-gray-500">(By category & customer type)</span>
                </div>
                {expandedSections.breakdown ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
              </button>

              {expandedSections.breakdown && (
                <div className="p-4 space-y-4">
                  {/* By Service Category */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                      By Service Category (Work/Stay/Play)
                    </h4>
                    {renderBreakdown(reportData.bookingRevenue?.breakdown, "serviceCategory")}
                  </div>

                  {/* By Organization Type */}
                  <div className="bg-gray-50 p-3 rounded-lg">
                    <h4 className="font-medium text-gray-800 mb-3 flex items-center gap-2">
                      <span className="w-2 h-2 bg-purple-500 rounded-full"></span>
                      By Customer Category (B2B/B2C)
                    </h4>
                    {renderBreakdown(reportData.bookingRevenue?.breakdown, "organizationType")}
                  </div>
                </div>
              )}
            </div>

            {/* Note */}
            <div className="text-xs text-gray-500 mt-4 p-3 bg-gray-50 rounded-lg">
              <strong>Note:</strong> The number of transactions and total value for revenue by source is equal to that
              for revenue share and equal to that for revenue by category.
            </div>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && !reportData && (
          <div className="text-center py-12 text-gray-500">No data available for the selected date range.</div>
        )}

        {/* Footer */}
        <div className="flex justify-end mt-6 pt-4 border-t">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default RevenueReportModal;
